import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { AppLayout } from './components/layout/AppLayout';
import { ScrollToTop } from './components/ui/ScrollToTop';
import { UpdateBanner } from './components/layout/UpdateBanner';
import { FullScreenLoader } from './components/ui/FullScreenLoader';

// Pages are split into their own chunks: a visitor of /login no longer downloads
// the admin pages, the charts or the portfolio.
const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((module) => ({ default: module[name] })));

const Login = named(() => import('./pages/Login'), 'Login');
const Register = named(() => import('./pages/Register'), 'Register');
const VerifyEmail = named(() => import('./pages/VerifyEmail'), 'VerifyEmail');
const ForgotPassword = named(() => import('./pages/ForgotPassword'), 'ForgotPassword');
const ResetPassword = named(() => import('./pages/ResetPassword'), 'ResetPassword');
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const SoldCards = lazy(() => import('./pages/SoldCards'));
const SetDetail = named(() => import('./pages/SetDetail'), 'SetDetail');
const PokemonDetail = named(() => import('./pages/PokemonDetail'), 'PokemonDetail');
const Discover = lazy(() => import('./pages/Discover'));
const Profile = named(() => import('./pages/Admin/Profile/Profile'), 'Profile');
const Friends = named(() => import('./pages/Friends'), 'Friends');
const FriendPortfolio = named(() => import('./pages/FriendPortfolio'), 'FriendPortfolio');
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/Admin/AdminUsers'));
const AdminUserDetail = lazy(() => import('./pages/Admin/AdminUserDetail'));
const AdminLogs = lazy(() => import('./pages/Admin/AdminLogs'));
const AdminSync = lazy(() => import('./pages/Admin/AdminSync'));
const AdminSetDetail = lazy(() => import('./pages/Admin/AdminSetDetail'));
const AdminCardQuality = lazy(() => import('./pages/Admin/AdminCardQuality'));

// Redirection racine vers Portfolio si connecté, sinon Login
function RootRedirect() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  return <Navigate to={isAuthenticated ? '/portfolio' : '/login'} replace />;
}

// Route "publique" : si connecté, on redirige vers /portfolio (bloque /login & /register)
function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (isAuthenticated) {
    return <Navigate to="/portfolio" replace />;
  }
  return <>{children}</>;
}

// Renders the Header and the sidebar except on the authentication pages
function AppInner() {
  const location = useLocation();

  // Remonter en haut de page lors du changement de route
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const hideHeader =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/verify-email' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password';

  return (
    <>
      {!hideHeader && <Header />}

      <AppLayout showSidebar={!hideHeader}>
        <Suspense fallback={<FullScreenLoader />}>
          <Routes>
            {/* Racine */}
            <Route path="/" element={<RootRedirect />} />

            {/* Auth (publiques, mais redirigées si déjà connecté) */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Pages légales (publiques) */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            {/* Dashboard (protégé) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            {/* Page principale : Portfolio (protégée) */}
            <Route
              path="/portfolio"
              element={
                <ProtectedRoute>
                  <Portfolio />
                </ProtectedRoute>
              }
            />

            {/* Sold cards (protégée) */}
            <Route
              path="/sold"
              element={
                <ProtectedRoute>
                  <SoldCards />
                </ProtectedRoute>
              }
            />

            {/* Set Detail (protégée) */}
            <Route
              path="/portfolio/set/:setId"
              element={
                <ProtectedRoute>
                  <SetDetail />
                </ProtectedRoute>
              }
            />

            {/* Toutes les cartes d'un Pokémon (protégée) */}
            <Route
              path="/pokemon/:dexId"
              element={
                <ProtectedRoute>
                  <PokemonDetail />
                </ProtectedRoute>
              }
            />

            {/* Découvrir (protégée) */}
            <Route
              path="/decouvrir"
              element={
                <ProtectedRoute>
                  <Discover />
                </ProtectedRoute>
              }
            />

            {/* Profile (protégée) */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            {/* Friends (protégée) */}
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <Friends />
                </ProtectedRoute>
              }
            />

            {/* Friend Portfolio (protégée) */}
            <Route
              path="/friends/:friendId/portfolio"
              element={
                <ProtectedRoute>
                  <FriendPortfolio />
                </ProtectedRoute>
              }
            />

            {/* Admin (protégé avec rôle admin) */}
            <Route path="/admin" element={<Navigate to="/admin/logs" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <AdminRoute>
                  <AdminUserDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <AdminRoute>
                  <AdminLogs />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/sync"
              element={
                <AdminRoute>
                  <AdminSync />
                </AdminRoute>
              }
            />
            {/* Images now live with the catalog they come from. */}
            <Route path="/admin/images" element={<Navigate to="/admin/sync" replace />} />
            <Route
              path="/admin/sets/:setId"
              element={
                <AdminRoute>
                  <AdminSetDetail />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/card-quality"
              element={
                <AdminRoute>
                  <AdminCardQuality />
                </AdminRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>

        {!hideHeader && <Footer />}
      </AppLayout>

      {/* Bouton Scroll to Top disponible sur toutes les pages */}
      <ScrollToTop />
      <UpdateBanner />
    </>
  );
}

// Configuration React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (remplace cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <SidebarProvider>
                <AppInner />
              </SidebarProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
